import React, { useMemo, useState } from 'react'
import classNames from 'classnames'
import { Dropdown, Form } from 'react-bootstrap'
import { type CrossClientQueryDataSource } from '../../../../models/CrossClient'
import { type IntegrationServiceId } from '../../../../common/integrations'
import { ReactComponent as ZocksIcon } from '../../../../assets/images/ZocksIcon-primary.svg'
import { useSupportedCrossClientIntegrations } from '../crossClientSettings'
import { getCrossClientIntegrationDisplayName } from '../../integrationDisplay'
import { CrossClientIntegrationIcon } from '../../CrossClientIntegrationIcon'

const _MAX_INTEGRATION_SOURCES = 2

interface CrossClientSourcesPillProps {
  sources: CrossClientQueryDataSource[]
  onChange: (sources: CrossClientQueryDataSource[]) => void
  disabled?: boolean
}

export const CrossClientSourcesPill: React.FC<CrossClientSourcesPillProps> = ({ sources, onChange, disabled }) => {
  const { supportedServiceIds, isLoading } = useSupportedCrossClientIntegrations()
  const [isOpen, setIsOpen] = useState(false)

  const selectedIntegrationIds = useMemo(
    () => sources.filter((s) => s.type === 'integration').map((s) => (s as { type: 'integration'; integrationType: IntegrationServiceId }).integrationType),
    [sources],
  )

  const toggleIntegration = (serviceId: IntegrationServiceId) => {
    const isSelected = selectedIntegrationIds.includes(serviceId)
    if (isSelected) {
      onChange(sources.filter((s) => !(s.type === 'integration' && s.integrationType === serviceId)))
      return
    }
    const integrationSources = sources.filter((s) => s.type === 'integration')
    if (integrationSources.length >= _MAX_INTEGRATION_SOURCES) {
      const firstIntegrationIndex = sources.findIndex((s) => s.type === 'integration')
      const filteredSources = sources.filter((_, index) => index !== firstIntegrationIndex)
      onChange([...filteredSources, { type: 'integration', integrationType: serviceId }])
      return
    }
    onChange([...sources, { type: 'integration', integrationType: serviceId }])
  }

  if (isLoading) {
    return null
  }

  const hasIntegrations = supportedServiceIds.length > 0

  return (
    <Dropdown autoClose='outside' onToggle={setIsOpen}>
      <Dropdown.Toggle as='button' bsPrefix='cross-client-pill' className='cross-client-pill d-flex gap-1 align-items-center' disabled={disabled}>
        <span className='paragraph-small bold'>Sources:</span>
        <span className='d-flex gap-1 align-items-center'>
          <ZocksIcon width='0.875rem' height='0.875rem' />
          {selectedIntegrationIds.map((serviceId) => (
            <CrossClientIntegrationIcon key={serviceId} serviceId={serviceId} className='cross-client-pill-icon' />
          ))}
        </span>
        {disabled ? null : <i className={classNames('bi paragraph-small zocks-text-gray-900', isOpen ? 'bi-chevron-contract' : 'bi-chevron-expand')} />}
      </Dropdown.Toggle>
      <Dropdown.Menu className='cross-client-popover'>
        <div className='paragraph-small'>Pick external sources to retrieve data that can't be found in Zocks.</div>
        {!hasIntegrations ? (
          <div className='paragraph-small zocks-text-gray-600'>No additional sources available</div>
        ) : (
          supportedServiceIds.map((serviceId) => {
            const isSelected = selectedIntegrationIds.includes(serviceId)
            return (
              <label key={serviceId} className='d-flex align-items-center gap-2 cross-client-popover-row mb-0'>
                <Form.Check className='d-flex' type='checkbox' checked={isSelected} onChange={() => toggleIntegration(serviceId)} />
                <span className='paragraph-small flex-grow-1'>{getCrossClientIntegrationDisplayName(serviceId)}</span>
                <CrossClientIntegrationIcon serviceId={serviceId} className='cross-client-popover-icon' />
              </label>
            )
          })
        )}
        {hasIntegrations ? (
          <div className='d-flex align-items-center gap-2 cross-client-popover-footer'>
            <i className='bi bi-info-square paragraph-small' />
            <span className='paragraph-small'>Additional sources increase query time.</span>
          </div>
        ) : null}
      </Dropdown.Menu>
    </Dropdown>
  )
}
